import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CashflowSnapshot } from '../hooks/useOverviewData';
import { formatCurrency } from '../utils/format';

interface Props {
    cashflow: CashflowSnapshot | null;
    cashflowPrevious?: CashflowSnapshot | null;
    loading: boolean;
}

const CashflowCard: React.FC<Props> = ({ cashflow, cashflowPrevious, loading }) => {
    const intl = useIntl();
    const locale = intl.locale;

    const isEmpty =
        cashflow != null &&
        cashflow.invoiced <= 0 &&
        cashflow.total_value <= 0 &&
        cashflow.agreement_count === 0;

    const rate = cashflow?.collection_rate_pct ?? null;
    const ringPct = Math.max(0, Math.min(100, rate ?? 0));

    // Compare-period delta on the collection rate (percentage points, not %).
    let rateDelta: number | null = null;
    if (
        cashflowPrevious != null &&
        cashflow != null &&
        cashflow.collection_rate_pct != null &&
        cashflowPrevious.collection_rate_pct != null
    ) {
        rateDelta = Math.round((cashflow.collection_rate_pct - cashflowPrevious.collection_rate_pct) * 10) / 10;
    }

    return (
        <section className="av2-card av2-cashflow">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">
                        <FormattedMessage
                            id="analyticsV2.cashflow.title"
                            defaultMessage="Cashflow"
                        />
                    </h3>
                    <p className="av2-card__subtitle">
                        <FormattedMessage
                            id="analyticsV2.cashflow.subtitle"
                            defaultMessage="Invoiced vs collected on agreements in this period"
                        />
                    </p>
                </div>
            </div>
            <div className="av2-card__body">
                {loading || !cashflow ? (
                    <CashflowSkeleton />
                ) : isEmpty ? (
                    <div className="av2-empty">
                        <FormattedMessage
                            id="analyticsV2.cashflow.empty"
                            defaultMessage="No agreements with billing data in this period."
                        />
                    </div>
                ) : (
                    <>
                        <div className="av2-cashflow__hero">
                            <CollectionRing pct={ringPct} hasRate={rate != null} />
                            <div className="av2-cashflow__hero-meta">
                                <span className="av2-cashflow__hero-label">
                                    <FormattedMessage
                                        id="analyticsV2.cashflow.collectedLabel"
                                        defaultMessage="Collected"
                                    />
                                </span>
                                <span className="av2-cashflow__hero-value">
                                    {rate != null
                                        ? `${formatRate(rate, locale)}%`
                                        : '—'}
                                </span>
                                {rateDelta != null && Math.abs(rateDelta) >= 0.1 && (
                                    <span
                                        className={`av2-cashflow__hero-delta av2-cashflow__hero-delta--${
                                            rateDelta > 0 ? 'up' : 'down'
                                        }`}
                                    >
                                        {rateDelta > 0 ? '↑' : '↓'} {formatRate(Math.abs(rateDelta), locale)}
                                        <FormattedMessage
                                            id="analyticsV2.cashflow.ppSuffix"
                                            defaultMessage=" pp"
                                        />
                                    </span>
                                )}
                            </div>
                        </div>
                        <ul className="av2-cashflow__breakdown">
                            <li className="av2-cashflow__row av2-cashflow__row--invoiced">
                                <span className="av2-cashflow__row-label">
                                    <span className="av2-cashflow__row-swatch" />
                                    <FormattedMessage
                                        id="analyticsV2.cashflow.totalInvoiced"
                                        defaultMessage="Total invoiced"
                                    />
                                </span>
                                <span className="av2-cashflow__row-value">
                                    {formatCurrency(cashflow.invoiced, locale)}
                                </span>
                            </li>
                            <li className="av2-cashflow__row av2-cashflow__row--collected">
                                <span className="av2-cashflow__row-label">
                                    <span className="av2-cashflow__row-swatch" />
                                    <FormattedMessage
                                        id="analyticsV2.cashflow.collected"
                                        defaultMessage="Collected"
                                    />
                                </span>
                                <span className="av2-cashflow__row-value">
                                    {formatCurrency(cashflow.collected, locale)}
                                </span>
                            </li>
                            <li className="av2-cashflow__row av2-cashflow__row--outstanding">
                                <span className="av2-cashflow__row-label">
                                    <span className="av2-cashflow__row-swatch" />
                                    <FormattedMessage
                                        id="analyticsV2.cashflow.outstanding"
                                        defaultMessage="Outstanding"
                                    />
                                </span>
                                <span className="av2-cashflow__row-value">
                                    {formatCurrency(cashflow.outstanding, locale)}
                                </span>
                            </li>
                            {cashflow.total_value > 0 && cashflow.total_value !== cashflow.invoiced && (
                                <li className="av2-cashflow__row av2-cashflow__row--contract">
                                    <span className="av2-cashflow__row-label">
                                        <span className="av2-cashflow__row-swatch" />
                                        <FormattedMessage
                                            id="analyticsV2.cashflow.contractValue"
                                            defaultMessage="Contract value"
                                        />
                                    </span>
                                    <span className="av2-cashflow__row-value">
                                        {formatCurrency(cashflow.total_value, locale)}
                                    </span>
                                </li>
                            )}
                        </ul>
                    </>
                )}
            </div>
        </section>
    );
};

const CollectionRing: React.FC<{ pct: number; hasRate: boolean }> = ({ pct, hasRate }) => {
    const RADIUS = 42;
    const STROKE = 10;
    const C = 2 * Math.PI * RADIUS;
    const dash = hasRate ? (pct / 100) * C : 0;

    return (
        <div className="av2-cashflow__ring">
            <svg viewBox="0 0 100 100" width="112" height="112" aria-hidden="true">
                <circle
                    cx="50"
                    cy="50"
                    r={RADIUS}
                    fill="none"
                    stroke="var(--av2-cashflow-ring-track)"
                    strokeWidth={STROKE}
                />
                {hasRate && (
                    <circle
                        cx="50"
                        cy="50"
                        r={RADIUS}
                        fill="none"
                        stroke="var(--av2-cashflow-ring-fill)"
                        strokeWidth={STROKE}
                        strokeLinecap="round"
                        strokeDasharray={`${dash} ${C - dash}`}
                        strokeDashoffset={C / 4}
                        transform="rotate(-90 50 50)"
                    />
                )}
            </svg>
        </div>
    );
};

const CashflowSkeleton: React.FC = () => (
    <>
        <div className="av2-cashflow__hero">
            <div className="av2-skel av2-skel--cashflow-ring" />
            <div className="av2-cashflow__hero-meta">
                <div className="av2-skel av2-skel--cashflow-label" />
                <div className="av2-skel av2-skel--cashflow-value" />
            </div>
        </div>
        <ul className="av2-cashflow__breakdown">
            {[0, 1, 2].map((i) => (
                <li key={i} className="av2-cashflow__row av2-cashflow__row--skeleton">
                    <div className="av2-skel av2-skel--cashflow-row-label" />
                    <div className="av2-skel av2-skel--cashflow-row-value" />
                </li>
            ))}
        </ul>
    </>
);

const formatRate = (n: number, locale: string): string => {
    return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 0,
    }).format(n);
};

export default CashflowCard;
